import type { UserId } from "@tento-lona/sheets";
import type { Option } from "@tento-lona/sheets";
import { TypedApi } from "@tento-core/typed-api";
import { Tracing } from "@tento-core/tracing";
import { Rational } from "@tento-core/rational";
import { UserOrderable } from "@tento-core/sort";
import { Field, Model, Scalar } from "@tento-core/model";

import { Network } from "../../network.ts";
import {
  Actor,
  type InvalidationSink,
  SheetDeleted,
  SheetListChanged,
  SheetMetadataChanged,
} from "../../invalidation.ts";
import type { SheetHandle } from "../../client/sheet-handle.ts";
import { SheetKey } from "../../client/sheet-key.ts";
import { type Json, Patch, SheetId, SheetKeyRef, SheetNetworkKey, WireDecode, WireDecodeError } from "@tento-lona/sheets";
import { Durable, DurableHandoffs } from "../../client/durable-handoffs.ts";
import {
  SdkRequestError,

  SheetReadError,
  SheetWriteError,
} from "../../errors.ts";
import { Ledger } from "@tento-lona/sheets/internal-sdk";
import type { ClientLifecycle } from "../../client/handle-errors.ts";
import { afterFailedWrite } from "../../client/after-failed-write.ts";
import { observedDurable } from "../../client/unobserved-settled.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

/**
 * One `list()` read in flight. `reads` holds an authoritative read
 * (`Ledger.beginRead` of the metadata ledger) of every sheet the answer may
 * overwrite under a write: each cached sheet and each sheet with a write in
 * flight when the list was sent, and each sheet a write begins on while it
 * is out. `touched` names the sheets a local change other than a metadata
 * write (a create, reorder, delete, restore, re-read or cache write) reached
 * while it was out: the answer may predate it, so it neither overwrites,
 * evicts nor re-inserts them.
 */
/**
 * How many more times a failed delete's recovery re-reads the sheet when a
 * write of the sheet reached it while it read (`SheetsClient.#owe`).
 */
const DELETE_REREADS = 2;

/**
 * A failed delete's recovery a newer write of the sheet dropped
 * (`SheetsClient.#owe`): read again once no metadata write of `sheet`
 * (its identity) is in flight.
 */
type OwedRecovery = {
  readonly sheet: string;
  readonly run: () => Promise<void>;
};

type ListRead = {
  readonly seq: number;
  readonly reads: Map<string, Ledger.Read<SheetMetadata>>;
  readonly touched: Set<string>;
};

/**
 * A JSON array answer decoded entry by entry with `parse`; anything but an
 * array is a `WireDecodeError` naming `type`. The list route declares no
 * response (its answer's shape follows the `?deleted` flag), so both of
 * its reads decode here.
 */
function decodeList<T>(type: string, raw: Json, parse: (entry: Json) => T): T[] {
  if (!Array.isArray(raw)) {
    throw new WireDecodeError(type, [Model.issue([], "type", "expected an array")]);
  }
  return raw.map((entry: Json) => parse(entry));
}

/** `SheetsClient`'s friend record (`Internals`): its constructor registers each instance. */
type SheetsClientInternals = {
  readonly cache: (list: SheetsClient) => SheetsClient.CacheWriters;
  readonly seedList: (list: SheetsClient, sheets: Network.UserSheet[]) => void;
  readonly reread: (list: SheetsClient, sheetKey: string) => Promise<void>;
};
const sheetsInternals = new Internals<SheetsClient, SheetsClientInternals>("SheetsClient");

/**
 * The writers of `list`'s local sheet cache (`SheetsClient.CacheWriters`): no
 * network I/O, no verdict. SDK-private — the client's own verbs and the
 * workspace-only `clientInstrumentationOf(client).sheetList` reach the
 * cache through it.
 *
 * @internal Not exported from `lib.ts`.
 */
export function sheetListCache(list: SheetsClient): SheetsClient.CacheWriters {
  return sheetsInternals.of(list).cache(list);
}

/**
 * Re-read one sheet from the server into `list`'s cache after a refused
 * durable update — through the sheet's pending-write model, so the read's
 * answer never erases a write still in flight or one that landed while the
 * read was out. Announced as `SheetsClient.ACTOR`. A failed read changes
 * nothing and rejects.
 *
 * @internal SDK-private (`LonaSdk.Client`'s durable-refusal routing).
 */
export function rereadSheetMetadata(list: SheetsClient, sheetKey: string): Promise<void> {
  return sheetsInternals.of(list).reread(list, sheetKey);
}

/**
 * Seed `list`'s local cache with the viewer's sheet list as the server
 * already sent it (a boot payload's `sheetList`), so `peek` / `peekAll`
 * answer before the first `list()`. Nothing is sent and nothing is
 * announced: the list is the server's own read, not an edit. Once per
 * list: a second seed throws.
 *
 * @internal SDK-private: the host door `platformOf(client).seedBoot` seeds
 * through it; no barrel exports it.
 */
export function seedSheetList(list: SheetsClient, sheets: Network.UserSheet[]): void {
  sheetsInternals.of(list).seedList(list, sheets);
}

/**
 * The viewer's sheet list: sheet CRUD, sharing, and the local list cache.
 * Accessed via `client.sheets`.
 *
 * Not to be confused with `SheetModelCache` (`@tento-lona/sheets/internal-sdk`), which
 * opens one sheet's row model and owns the shared canonical-cell store; the
 * client reaches it through `platformOf(client).sheet` / `peekSheet`.
 *
 * Reads (`list`, `listDeleted`, `get`, `getAcl`) throw `SheetReadError`;
 * writes throw `SheetWriteError`. `updateDurable` is the one durable
 * write (`Durable`: applied at once, `settled` on the verdict).
 *
 * Every write names its `actor`, which tags the invalidations it emits
 * (`Actor.EXTERNAL` for a caller with no echo of its own to recognise).
 */
export class SheetsClient {
  static readonly #internals: SheetsClientInternals = {
    cache: (list) => ({
      insert: (sheet) => {
        list.#touch(sheet.id);
        list.#insertLocal(sheet);
      },
      batchInsert: (sheets) => {
        for (const sheet of sheets) list.#touch(sheet.id);
        list.#batchInsertLocal(sheets);
      },
      update: (id, patch) => {
        list.#touch(id);
        return list.#updateLocal(id, patch);
      },
      remove: (id) => {
        list.#touch(id);
        list.#removeLocal(id);
      },
    }),
    reread: (list, sheetKey) => list.#reread(sheetKey),
    seedList: (list, sheets) => {
      if (list.#seeded) throw new Error("SheetsClient: the sheet list was already seeded — seed once, at boot");
      list.#seeded = true;
      for (const sheet of sheets) list.#touch(sheet.id);
      list.#batchInsertLocal(sheets);
    },
  };

  /** Whether a boot seed ran (`seedSheetList`): a second one throws. */
  #seeded = false;

  static readonly ACTOR = new Actor("sheets-accessor");
  /**
   * The echo tag on the copy-on-write PROMOTE's `create`. A promote is a
   * transparent in-place re-key — the open sheet's cell values are unchanged —
   * so a host's sheet-invalidation dispatch skips the whole-sheet `rebindAll`
   * for a `SheetListChanged` carrying this actor (an unfiltered rebind blurs
   * the focused editor and drops the caret). The sidebar list still refreshes:
   * it subscribes by path (`"sheets"`), not by actor. A host matches it by
   * `actor.name` in its `cell-content-all` dispatch. It only filters that
   * echo: an app learns that a sheet was promoted, and its new id, from
   * `ClientConfig.onSheetPromoted` (`SheetPromoted`).
   */
  static readonly PROMOTE_ACTOR = new Actor("sheet-promote");
  readonly #byId = new Map<string, Readonly<Network.UserSheet>>();
  /**
   * Lookup key → the id of the cached sheet that owns it: an index derived
   * from `byId`, never a second copy of an entry. Every cache writer keeps
   * it reconciled (`#indexLookupKey`, `#retireLookupKey`): a re-keyed entry
   * retires its old key, and removing a sheet removes only a key it still
   * owns.
   */
  readonly #byLookupKey = new Map<string, string>();
  #sortedCache: Option<readonly Readonly<Network.UserSheet>[]> = null;
  readonly #api: TypedApi<Network.Routes>;
  readonly #invalidation: InvalidationSink;
  /** The metadata writes in flight — checked `update` and `updateDurable`
   *  alike — per sheet key: one pending-write model for both twins. */
  /** Issue numbers of `reorder`, client-wide: a recanonicalized answer to
   *  one sheet's move renumbers the others too. */
  #reorderSeq = 0;
  /** Per sheet (its identity): the issue number of the reorder whose
   *  answer the cached position came from. The server applies reorders in
   *  send order, so an answer only installs over an older one's. */
  readonly #reorderInstalled = new Map<string, number>();
  /** The metadata writes in flight (`update` and `updateDurable` alike), per
   *  sheet identity: the sheet-metadata instance of the one ledger (`Ledger`,
   *  a pure model — this list shows its projection itself). */
  readonly #updatesInFlight = new Ledger<SheetMetadata>(SHEET_PATCH, {
    sinks: () => [],
    name: "sheets-update",
    takeBackContext: "the take-back of a refused sheet update",
    describe: (sheet) => ({ sheet }),
    live: () => !this.#lifecycle.disposed,
    abandoned: (sheet, fields, error) =>
      Tracing.warn("net")("sheets-update/take-back-abandoned", {
        sheet,
        fields: [...fields].join(","),
        error: error.message,
      }),
  });
  /** Per sheet key: how many metadata writes are in flight (`#beginWrite`). */
  readonly #writesInFlight = new Map<string, number>();
  /** The `list()` reads in flight. */
  readonly #listReads = new Set<ListRead>();
  /** The recovery re-reads in flight (`#reread`, `#recoverDelete`): each
   *  collects the sheets a write of this list touched while it was out
   *  (`#touch`, `#beginWrite` — a reorder accepted, an update begun, a
   *  cache write), which its answer may predate: the one read barrier of
   *  every sheet recovery. A sheet is collected under its canonical
   *  identity (`#identityOf`) as well as the key that reached it, so a
   *  write addressed by id and a read addressed by lookup key meet. */
  readonly #rereads = new Set<Set<string>>();
  /** The delete recoveries a newer write of their sheet dropped, each owed once that write settled (`#owe`). */
  readonly #owed = new Set<OwedRecovery>();
  /** The issue number of the last `list()` sent / whose answer was applied:
   *  an answer older than one already applied is not applied. */
  #listSeq = 0;
  #appliedListSeq = 0;
  /** The owning client's liveness: every network verb checks it first. */
  readonly #lifecycle: ClientLifecycle;

  /**
   * @param onSheetDeleted Evicts the deleted sheet's LIVE handle (all
   * `openedHandles` aliases) + its rows-side cache. `delete` only clears
   * the server-list cache here; without this eviction a re-open returns the
   * stale promoted `synced` handle and the sheet can't be re-COWed in-session.
   */
  readonly #durable: DurableHandoffs;
  readonly #onSheetDeleted: Option<(sheetId: SheetId) => void>;
  constructor(
    api: TypedApi<Network.Routes>,
    invalidation: InvalidationSink,
    /** The client's durable writes in flight; a refused `updateDurable`
     *  rejects through it. */
    durable: DurableHandoffs,
    onSheetDeleted: Option<(sheetId: SheetId) => void>,
    /** The owning client's liveness (`LonaSdk.Client.Lifecycle`): after
     *  its `dispose`, every network verb throws
     *  `LonaSdk.Client.DisposedError`. */
    lifecycle: ClientLifecycle,
  ) {
    sheetsInternals.register(this, SheetsClient.#internals);
    this.#onSheetDeleted = onSheetDeleted;
    this.#durable = durable;
    this.#lifecycle = lifecycle;
    this.#api = api;
    this.#invalidation = invalidation;
  }

  // ── Remote API ────────────────────────────────────────────────────────

  /**
   * The viewer's live sheets, read from the server; the local cache is
   * REPLACED by them, so once it resolves `peek` / `peekAll` /
   * `peekFirst` answer the server's list — a cached sheet the answer does
   * not hold is evicted (announced as one `SheetListChanged`, actor
   * `SheetsClient.ACTOR`) — with the metadata writes still in flight
   * (`update`, `updateDurable`) laid over it. The answer goes through the
   * writes' pending model, as a re-read does: a write that landed while the
   * list was out is not erased by the answer (which may predate it), and a
   * later refusal restores the listed value. A sheet created, reordered,
   * deleted or restored while the list was out keeps its local state; an
   * answer older than one already applied changes nothing. Nothing else is
   * announced (the list is the server's own read, not an edit). Throws
   * `SheetReadError("list")` on failure (nothing changed), and
   * `LonaSdk.Client.DisposedError` when the client was disposed before the
   * answer (nothing cached).
   */
  async list(): Promise<readonly Readonly<Network.UserSheet>[]> {
    this.#lifecycle.assertLive("sheets.list");
    const listRead: ListRead = { seq: ++this.#listSeq, reads: new Map(), touched: new Set() };
    for (const key of new Set([...this.#byId.keys(), ...this.#writesInFlight.keys()])) {
      listRead.reads.set(key, this.#updatesInFlight.beginRead(key));
    }
    this.#listReads.add(listRead);
    let sheets: readonly Readonly<Network.UserSheet>[];
    try {
      sheets = await SheetReadError.check(
        "list",
        "GET",
        "/api/sheets/list",
        async () =>
          (await this.#api.getResult("/api/sheets/list", { query: { deleted: null } }))
            .map((raw) => Object.freeze(decodeList("UserSheet", raw, Network.UserSheet.parse).map(frozenSheet))),
      );
    } catch (err) {
      this.#closeListRead(listRead);
      throw err;
    }
    if (this.#lifecycle.disposed || listRead.seq < this.#appliedListSeq) {
      this.#closeListRead(listRead);
      this.#lifecycle.assertLive("sheets.list");
      return sheets;
    }
    this.#appliedListSeq = listRead.seq;
    this.#applyList(sheets, listRead);
    return sheets;
  }

  /** Close a `list()` read without applying its answer. */
  #closeListRead(listRead: ListRead): void {
    this.#listReads.delete(listRead);
    for (const read of listRead.reads.values()) this.#updatesInFlight.settleRead(read, null);
    listRead.reads.clear();
  }

  /** Replace the cache with a `list()` answer (see `list`). */
  #applyList(sheets: readonly Readonly<Network.UserSheet>[], listRead: ListRead): void {
    this.#listReads.delete(listRead);
    const answered = new Set<string>();
    for (const sheet of sheets) {
      answered.add(sheet.id);
      const read = listRead.reads.get(sheet.id) ?? null;
      listRead.reads.delete(sheet.id);
      if (listRead.touched.has(sheet.id)) {
        if (read != null) this.#updatesInFlight.settleRead(read, null);
        continue;
      }
      const reread = read == null
        ? null
        : this.#updatesInFlight.settleRead(read, attributesOfSheet(sheet));
      this.#insertLocal(reread == null ? sheet : { ...sheet, ...reread });
    }
    for (const read of listRead.reads.values()) this.#updatesInFlight.settleRead(read, null);
    listRead.reads.clear();
    let evicted = false;
    for (const key of [...this.#byId.keys()]) {
      if (answered.has(key) || listRead.touched.has(key)) continue;
      this.#removeLocal(key);
      evicted = true;
    }
    if (evicted) this.#invalidation.emit(new SheetListChanged(SheetsClient.ACTOR));
  }

  /** A local change other than a metadata write reached `key`: no `list()`
   *  answer in flight may overwrite, evict or re-insert it. */
  #touch(key: string): void {
    const identity = this.#identityOf(key);
    for (const listRead of this.#listReads) {
      listRead.touched.add(key);
      listRead.touched.add(identity);
    }
    this.#touchRereads(key);
  }

  /** `key` changed under the recovery re-reads out now (`#rereads`): each
   *  collects it and the sheet identity it names (`#identityOf`). */
  #touchRereads(key: string): void {
    if (this.#rereads.size === 0) return;
    const identity = this.#identityOf(key);
    for (const touched of this.#rereads) {
      touched.add(key);
      touched.add(identity);
    }
  }

  /**
   * The canonical identity of the sheet `sheetKey` (a `#keyOf` key) names:
   * the cached entry's id — found by its id in any spelling, or by its
   * lookup key as `peek` finds it (a `[user]:lookup` key only when the
   * entry's creator is that user) — else `sheetKey` itself (the cache does
   * not hold it). Every verb's cache writes, pending-write model and
   * recovery barrier key by it; the wire keeps the spelling the caller
   * addressed.
   */
  #identityOf(sheetKey: string): string {
    if (this.#byId.has(sheetKey)) return sheetKey;
    const ref = SheetKeyRef.of(sheetKey);
    if (ref.kind === "sheetId") return this.#cachedKeyOf(ref.sheetId) ?? sheetKey;
    const id = this.#byLookupKey.get(ref.lookupKey) ?? null;
    if (id == null) return sheetKey;
    if (ref.userId != null && this.#byId.get(id)?.creatorId !== ref.userId.toString()) return sheetKey;
    return id;
  }

  /** Open a recovery re-read's barrier (`#rereads`); close it with `delete`. */
  #openBarrier(): Set<string> {
    const touched = new Set<string>();
    this.#rereads.add(touched);
    return touched;
  }

  /** Enter a metadata write on `sheetKey`: every `list()` in flight reads
   *  the sheet under it (`ListRead`). */
  #beginWrite(
    sheetKey: string,
    attributes: Network.UserSheetAttributes,
    actor: Actor,
  ): Ledger.Write<SheetMetadata> {
    for (const listRead of this.#listReads) {
      if (!listRead.reads.has(sheetKey)) {
        listRead.reads.set(sheetKey, this.#updatesInFlight.beginRead(sheetKey));
      }
    }
    this.#writesInFlight.set(sheetKey, (this.#writesInFlight.get(sheetKey) ?? 0) + 1);
    // A recovery re-read out now may predate this write (`#rereads`).
    this.#touchRereads(sheetKey);
    return this.#updatesInFlight.begin(sheetKey, attributes, actor, {
      correction: SheetsClient.ACTOR,
      quiet: false,
      capture: () => this.#attributesOf(sheetKey),
    });
  }

  /** Settle a metadata write on its `verdict`; answers the sheet as the
   *  settle left it (`Ledger.settle`: a refusal's fields go back to its
   *  `projection`). */
  #settleWrite(write: Ledger.Write<SheetMetadata>, verdict: Ledger.Verdict): Ledger.Settled<SheetMetadata> {
    const left = (this.#writesInFlight.get(write.key) ?? 1) - 1;
    if (left <= 0) this.#writesInFlight.delete(write.key);
    else this.#writesInFlight.set(write.key, left);
    const settled = this.#updatesInFlight.settle(write, verdict);
    if (left <= 0) this.#runOwed();
    return settled;
  }

  /** The checked PATCH of `sheetKey` (the caller's spelling): one send of the metadata ledger. */
  #patchTransport(sheetKey: string): Ledger.Transport<SheetMetadata> {
    return (body) =>
      SheetWriteError.check(
        "update",
        "PATCH",
        "/api/sheets/{sheetKey}",
        () =>
          this.#api.patchResult("/api/sheets/{sheetKey}", {
            path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) },
            body,
          }),
      );
  }

  /**
   * A delete's recovery re-read answered while a write of its sheet reached
   * it (the `#rereads` barrier): the answer may predate that write, so it
   * decides nothing — and the write may yet be refused (the sheet it
   * addressed is gone), leaving the deleted sheet cached. The recovery is
   * owed instead of dropped: `recover` runs again, with one re-read fewer
   * left, once no metadata write of `sheet` (its identity) is in flight
   * (now, when those settled while the read was out), and only while the
   * client is live. With no re-read left the cache is left as is.
   */
  #owe(sheet: string, rereads: number, recover: (rereads: number) => Promise<void>): Promise<void> {
    if (rereads <= 0) return Promise.resolve();
    const run = (): Promise<void> => recover(rereads - 1);
    if (!this.#writesInFlight.has(sheet)) return run();
    this.#owed.add({ sheet, run });
    return Promise.resolve();
  }

  /** Start every owed recovery (`#owe`) whose sheet no metadata write is in flight for — while the client is live. */
  #runOwed(): void {
    for (const owed of [...this.#owed]) {
      if (this.#writesInFlight.has(owed.sheet)) continue;
      this.#owed.delete(owed);
      if (this.#lifecycle.disposed) continue;
      void owed.run().catch((failure: unknown) =>
        Tracing.warn("net")(`sheets.delete: the recovery owed after a newer write settled failed: ${String(failure)}`)
      );
    }
  }

  /**
   * List the active viewer's soft-deleted sheets. Each entry has
   * `deleted: true`. Ordered by deletion time (most-recent first).
   * Throws `SheetReadError("list")` on failure.
   *
   * Each entry is decoded (`SheetsClient.DeletedSheetSummary.parse`):
   * an answer that does not decode throws `SheetReadError("list")` with
   * outcome `"malformed"`. (The route answers `UserSheet[]` or
   * `DeletedSheetSummary[]` by its `?deleted` flag, a union the generated
   * route map cannot state, so it declares no response and both reads decode
   * here.)
   */
  async listDeleted(): Promise<SheetsClient.DeletedSheetSummary[]> {
    this.#lifecycle.assertLive("sheets.listDeleted");
    return SheetReadError.check(
      "list",
      "GET",
      "/api/sheets/list",
      async () =>
        (await this.#api.getResult("/api/sheets/list", { query: { deleted: true } }))
          .map((raw) =>
            decodeList(
              "DeletedSheetSummary",
              raw,
              SheetsClient.DeletedSheetSummary.parse,
            )
          ),
    );
  }

  /**
   * One sheet with its rows, read from the server; `null` when the server
   * answers 404. Any other failure throws `SheetReadError("get")`. `sheet`
   * is a held list entry, an open handle or a typed key
   * (`SheetsClient.SheetRef`), sent as `update` sends it.
   */
  async get(sheet: SheetsClient.SheetRef): Promise<Option<Network.UserSheetWithRows>> {
    this.#lifecycle.assertLive("sheets.get");
    return this.#getByKey(this.#keyOf(sheet));
  }

  /** `get` of a key already spelled for the wire. */
  async #getByKey(sheetKey: string): Promise<Option<Network.UserSheetWithRows>> {
    return SheetReadError.check(
      "get",
      "GET",
      "/api/sheets/{sheetKey}",
      () =>
        SdkRequestError.notFoundAsNull(() =>
          this.#api.getResult("/api/sheets/{sheetKey}", { path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) } })
        ),
    );
  }

  /**
   * Create a sheet; `actor` tags the `SheetListChanged` it emits. Throws
   * `SheetWriteError("create")` — its `status` is the
   * numeric HTTP status — when the server rejects the sheet, does not answer,
   * or answers without a sheet body; nothing is cached locally then. When
   * the failure may have stored the sheet (`SdkRequestError.mayHaveStored`:
   * no verdict, or an answer the SDK could not read) the sheet list is
   * re-read (`list()`) and announced as one `SheetListChanged`
   * (`SheetsClient.ACTOR`), so a sheet the server did create shows.
   */
  async create(
    sheet: Network.NewUserSheet,
    actor: Actor,
  ): Promise<Network.UserSheetWithRows> {
    this.#lifecycle.assertLive("sheets.create");
    const body = Network.NewUserSheet.make(sheet);
    let created: Network.UserSheetWithRows;
    try {
      const answer = await SheetWriteError.check(
        "create",
        "POST",
        "/api/sheets",
        async () => {
          const result = await this.#api.postResult("/api/sheets", { body });
          const error = result.err();
          if (error != null) {
            // Surface the raw server body for diagnosis — `reason`/`details`
            // are only populated for the structured ServiceError shape; a
            // deserialization 422 carries a plain-text body instead.
            Tracing.warn("net")(
              `POST /api/sheets ${error.code}: ${JSON.stringify(error.inner)}`,
            );
          }
          return result;
        },
      );
      if (!answer) {
        throw new SheetWriteError({
          step: "create",
          method: "POST",
          path: "/api/sheets",
          // Answered 2xx with no body: the sheet may exist, but the answer
          // cannot be read.
          failure: { outcome: "malformed" },
          details: "the server answered without a sheet body",
        });
      }
      created = answer;
    } catch (err) {
      // No verdict (or one the SDK could not read): the sheet may exist —
      // re-read the list so it shows.
      afterFailedWrite(err, this.#lifecycle, "sheets.create", () => this.#relist());
      throw err;
    }
    this.#touch(created.id);
    this.#insertLocal(created);
    this.#invalidation.emit(new SheetListChanged(actor));
    return created;
  }

  /**
   * Update sheet metadata, CHECKED — the twin of `updateDurable`: the cache
   * is updated and `SheetMetadataChanged` emitted (as `actor`) at once, then
   * `PATCH /api/sheets/{sheetKey}` is sent and awaited. A refused or
   * unanswered PATCH takes back only its OWN fields — back to the sheet's
   * confirmed state with every other update still in flight replayed in
   * issue order (the metadata ledger, `Ledger`, shared with `updateDurable`), so a
   * refused older update never erases a newer one that landed, checked or
   * durable — announces the take-back as `SheetsClient.ACTOR` (so the
   * writing surface, which ignores its own echo, repaints the correction),
   * and rejects with `SheetWriteError("update")`.
   *
   * ```ts
   * await client.sheets.update(entry, { title, description: Patch.clear }, actor);
   * ```
   *
   * `patch` is a `SheetsClient.Patch` — the SDK's one patch vocabulary:
   * a field it names is set, or cleared with `Patch.clear`; an omitted one
   * is left as it is. A sheet's position is not metadata: `reorder` is the
   * one mover.
   */
  async update(
    sheet: SheetsClient.SheetRef,
    patch: SheetsClient.Patch,
    actor: Actor,
  ): Promise<void> {
    this.#lifecycle.assertLive("sheets.update");
    const attributes = SheetsClient.patchBody(patch);
    const sheetKey = this.#keyOf(sheet);
    const identity = this.#identityOf(sheetKey);
    const write = this.#beginWrite(identity, attributes, actor);
    this.#updateLocal(identity, attributes);
    this.#invalidation.emit(new SheetMetadataChanged(actor, this.#refOf(sheetKey)));
    try {
      // The body is the ledger's: this write's fields of the projection at
      // send time (its own patch, sent at once).
      await this.#updatesInFlight.send(write, this.#patchTransport(sheetKey));
    } catch (err) {
      this.#takeBack(write, err, "sheets.update");
      // No verdict (or one the SDK could not read): the PATCH may have
      // landed — re-read the sheet, as the durable twin's refusal does.
      afterFailedWrite(err, this.#lifecycle, "sheets.update", () => this.#reread(sheetKey));
      throw err;
    }
    this.#settleWrite(write, Ledger.ACCEPTED);
  }

  /**
   * Settle a refused metadata write: put the fields it wrote back (the
   * confirmed state with every surviving write replayed) and announce the
   * correction as `SheetsClient.ACTOR` — never as the writer, whose
   * surface ignores its own echo.
   */
  #takeBack(write: Ledger.Write<SheetMetadata>, err: unknown, context: string): void {
    const settled = this.#settleWrite(write, { accepted: false, error: err, context });
    if (settled.projection == null) return;
    const restore = SHEET_PATCH.body(settled.projection, write.fields);
    this.#updateLocal(write.key, restore);
    this.#invalidation.emit(new SheetMetadataChanged(SheetsClient.ACTOR, this.#refOf(write.key)));
  }

  /**
   * `rereadSheetMetadata`: the read, laid under the writes in flight (and
   * under a write that reached the sheet while it was out — a `reorder`
   * accepted, a cache write: the cached position stands; the `#rereads`
   * barrier, keyed by the sheet's identity whether `sheetKey` spells its id
   * or its lookup key). A recovery the client's disposal overtook sends
   * nothing; an answer that arrives after the client was disposed is
   * dropped (the read closed as a failed one, nothing installed or
   * announced).
   */
  async #reread(sheetKey: string): Promise<void> {
    if (this.#lifecycle.disposed) return;
    const identity = this.#identityOf(sheetKey);
    const touched = this.#openBarrier();
    const read = this.#updatesInFlight.beginRead(identity);
    let fresh: Option<Network.UserSheetWithRows> = null;
    try {
      fresh = await this.#getByKey(sheetKey);
    } finally {
      this.#rereads.delete(touched);
      if (this.#lifecycle.disposed) fresh = null;
      const confirmed = fresh == null ? null : attributesOfSheet(fresh);
      const reread = this.#updatesInFlight.settleRead(read, confirmed);
      if (fresh != null && reread != null) {
        const { rows: _rows, ...sheet } = fresh;
        const shown: Network.UserSheet = { ...sheet, ...reread };
        // A write that reached the sheet while the read was out (a reorder
        // accepted) may be missing from its answer: the cached position stands.
        const cached = this.#byId.get(sheet.id) ?? null;
        if (cached != null && (touched.has(identity) || touched.has(sheet.id))) shown.position = cached.position;
        this.#touch(sheet.id);
        this.#insertLocal(shown);
        this.#invalidation.emit(new SheetMetadataChanged(SheetsClient.ACTOR, this.#refOf(sheetKey)));
      }
    }
  }

  /** The cached sheet's writable metadata — the confirmed base a checked
   *  `update` captures. */
  #attributesOf(sheetKey: string): Network.UserSheetAttributes {
    const sheet = this.#byId.get(sheetKey);
    return sheet == null ? {} : attributesOfSheet(sheet);
  }

  /**
   * Update sheet metadata DURABLY through the client's debounce executor
   * (`ClientConfig.debounceExecutor`): the cache is updated and
   * `SheetMetadataChanged` emitted at once, and the PATCH is handed off.
   * `applied` says what happened locally: `Durable.shown(undefined)` when
   * the list cache holds the sheet (the change shows now), `on-settle` when
   * it does not (nothing to show; the next `list()` reads the server's
   * value). Returns at once (`Durable`): `settled` resolves when the execution that
   * carried the PATCH answered without refusing it, and rejects with
   * `SheetWriteError("update")` when it was refused or went unanswered —
   * its own fields taken back as `update` takes them back (the pending-write
   * model is shared, so a refusal of either twin never erases a newer write
   * of the other that landed), and the sheet re-read by the client. The
   * caller owns `settled`. Paints first. A durable verb never throws: on a
   * disposed client `settled` rejects with `LonaSdk.Client.DisposedError`,
   * nothing applied (`applied` is `on-settle`). `patch` is a
   * `SheetsClient.Patch`, as `update` takes it (no position: `reorder`
   * moves a sheet).
   */
  updateDurable(
    sheet: SheetsClient.SheetRef,
    patch: SheetsClient.Patch,
    actor: Actor,
  ): Durable<void> {
    try {
      this.#lifecycle.assertLive("sheets.updateDurable");
    } catch (err) {
      // A durable verb never throws: the disposed client's refusal is its
      // `settled` rejection, nothing applied.
      return observedDurable(this.#lifecycle, { applied: Durable.ON_SETTLE, settled: Promise.reject(err) });
    }
    const attributes = SheetsClient.patchBody(patch);
    const sheetKey = this.#keyOf(sheet);
    const identity = this.#identityOf(sheetKey);
    const write = this.#beginWrite(identity, attributes, actor);
    const shown = this.#updateLocal(identity, attributes) != null;
    this.#invalidation.emit(new SheetMetadataChanged(actor, this.#refOf(sheetKey)));
    const settled = this.#updatesInFlight.send(write, (body) => this.#persistDurable(sheetKey, body)).then(
      () => {
        this.#settleWrite(write, Ledger.ACCEPTED);
      },
      (err: unknown) => {
        this.#takeBack(write, err, "sheets.updateDurable");
        throw err;
      },
    );
    return observedDurable(this.#lifecycle, { applied: shown ? Durable.shown(undefined) : Durable.ON_SETTLE, settled });
  }

  /** Hand one sheet PATCH to the executor; throw its refusal. */
  async #persistDurable(
    sheetKey: string,
    attributes: Network.UserSheetAttributes,
  ): Promise<void> {
    // Sheet AND written fields: a `{ title }` and a `{ description }` update
    // are sent separately, never one body replacing the other.
    const route = SheetNetworkKey.fromWire(sheetKey);
    const key = DurableHandoffs.sheetPatchKey(route, attributes);
    const requestId = this.#durable.nextRequestId();
    const failure = await this.#durable.handOff(key, requestId, () =>
      this.#api.debounce.patch("/api/sheets/{sheetKey}", {
        path: { sheetKey: route },
        body: attributes,
        options: { key, requestId },
      }));
    if (failure == null) return;
    const verdict = DurableHandoffs.verdict(failure);
    throw new SheetWriteError({
      step: "update",
      method: "PATCH",
      path: "/api/sheets/{sheetKey}",
      failure: verdict,
      reason: failure.reason ?? "",
      details: failure.details ?? "",
    });
  }


  /**
   * Reorder a sheet's position, CHECKED. Resolves with what the server did
   * (`SheetsClient.ReorderResult`): `{ kind: "moved" }` — only this
   * sheet's position changed, and the cached entry has it; or
   * `{ kind: "recanonicalized", sheets }` — the server reassigned every
   * position (`sheets`), and the cached list was refreshed with them. Both
   * are announced as `actor`. Throws `SheetWriteError("reorder")` (nothing
   * changed locally) when the server rejects the move or does not answer;
   * when the failure may have stored the move (`SdkRequestError.mayHaveStored`)
   * the list is re-read (`list()`, every position the server holds) and
   * announced as one `SheetListChanged` (`SheetsClient.ACTOR`).
   *
   * Reorders are stamped with an issue number: an answer installs a sheet's
   * position only over one from an OLDER reorder, so two reorders whose
   * answers arrive reversed leave the cache on the newer one (the server
   * applied them in send order), and a recanonicalized list answering an
   * older move never overwrites a sheet a newer answer already placed.
   */
  async reorder(
    sheet: SheetsClient.SheetRef,
    opts: { position: Rational; recanonicalizationThreshold: Option<number> },
    actor: Actor,
  ): Promise<SheetsClient.ReorderResult> {
    this.#lifecycle.assertLive("sheets.reorder");
    const sheetKey = this.#keyOf(sheet);
    const seq = ++this.#reorderSeq;
    let response: Network.SheetReorderResponse;
    try {
      const answer = await SheetWriteError.check(
        "reorder",
        "POST",
        "/api/sheets/{sheetKey}/reorder",
        () =>
          this.#api.postResult("/api/sheets/{sheetKey}/reorder", {
            path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) },
            body: {
              position: opts.position,
              recanonicalizationThreshold: opts.recanonicalizationThreshold,
            },
          }),
      );
      if (answer == null) {
        // Answered 2xx with no body: the move may have landed, but the
        // answer cannot be read — nothing changed locally.
        throw new SheetWriteError({
          step: "reorder",
          method: "POST",
          path: "/api/sheets/{sheetKey}/reorder",
          failure: { outcome: "malformed" },
          details: "the server answered without a body",
        });
      }
      response = answer;
    } catch (err) {
      // No verdict (or one the SDK could not read): the move may have
      // landed — re-read every position.
      afterFailedWrite(err, this.#lifecycle, "sheets.reorder", () => this.#relist());
      throw err;
    }
    if (response.recanonicalizedSheets) {
      // Only the sheets no newer reorder's answer placed already.
      const fresh = response.recanonicalizedSheets.filter((moved) => this.#claimReorder(moved.id, seq));
      for (const moved of fresh) this.#touch(moved.id);
      this.#batchInsertLocal(fresh);
      this.#invalidation.emit(new SheetListChanged(actor));
      return { kind: "recanonicalized", sheets: response.recanonicalizedSheets };
    }
    // Update position locally
    const identity = this.#identityOf(sheetKey);
    const cached = this.#byId.get(identity) ?? null;
    this.#touch(identity);
    // A new entry, never the held one changed in place: a sheet a caller
    // read before keeps the position it was read with. An older reorder's
    // answer arriving after a newer one's installs nothing.
    if (cached && this.#claimReorder(identity, seq)) this.#insertLocal({ ...cached, position: opts.position });
    this.#invalidation.emit(new SheetMetadataChanged(actor, this.#refOf(sheetKey)));
    return { kind: "moved" };
  }

  /**
   * Delete a sheet — unconditional and silent: it asks no one and shows
   * nothing (a caller that confirms with its user does so before calling
   * it, and the notice after it — "Sheet deleted" with its undo — is the
   * caller's, built from what this resolves). Resolves once the server
   * deleted it and it was removed locally (`SheetDeleted`, then
   * `SheetListChanged`, both as `actor`), with a `SheetsClient.Deletion`
   * `{ outcome: "deleted", undo }`: `undo(actor)` restores the sheet
   * (`POST /api/sheets/{sheetKey}/restore`) and announces the list as
   * `actor`; it rejects with `SheetWriteError("restore")` when the server
   * refuses, and with `LonaSdk.Client.DisposedError` after
   * `client.dispose()`. Throws `SheetWriteError("delete")` — before any
   * local change — when the server rejects the delete or does not answer;
   * unanswered (`SdkRequestError.mayHaveStored`), the sheet is re-read and,
   * when the server no longer holds it, removed as a landed delete removes
   * it, announced as `SheetsClient.ACTOR` (no undo: the delete still
   * rejects). Throws `LonaSdk.Client.DisposedError` — nothing sent,
   * nothing changed — when the client was disposed.
   */
  async delete(sheet: SheetsClient.SheetRef, actor: Actor): Promise<SheetsClient.Deletion> {
    this.#lifecycle.assertLive("sheets.delete");
    return this.#deleteSheet(this.#keyOf(sheet), "delete", actor);
  }

  /**
   * Reset a forked built-in sheet — unconditional and silent, as `delete`
   * is (the caller confirms first and shows the notice). Resolves once the
   * fork is deleted and removed locally, with a `SheetsClient.Deletion`
   * `{ outcome: "reset", undo }` (`undo(actor)` restores the fork, as
   * `delete`'s does). Throws `SheetWriteError("reset")` — before any local
   * change — on a server failure (re-read as `delete` re-reads when it may
   * have landed), and `LonaSdk.Client.DisposedError` (nothing sent or
   * changed) when the client was disposed.
   */
  async resetBuiltinFork(
    sheet: SheetsClient.SheetRef,
    actor: Actor,
  ): Promise<SheetsClient.Deletion> {
    this.#lifecycle.assertLive("sheets.resetBuiltinFork");
    return this.#deleteSheet(this.#keyOf(sheet), "reset", actor);
  }

  /** Whether the answer of reorder `seq` may place sheet `key`: no newer
   *  reorder's answer placed it. Claims it when so. */
  #claimReorder(key: string, seq: number): boolean {
    const identity = this.#identityOf(key);
    if ((this.#reorderInstalled.get(identity) ?? 0) > seq) return false;
    this.#reorderInstalled.set(identity, seq);
    return true;
  }

  /** `delete` / `resetBuiltinFork`: `step` is the one the failure reports. */
  async #deleteSheet(
    sheetKey: string,
    step: "delete" | "reset",
    actor: Actor,
  ): Promise<SheetsClient.Deletion> {
    const identity = this.#identityOf(sheetKey);
    const removed = this.#byId.get(identity) ?? null;
    try {
      await SheetWriteError.check(
        step,
        "DELETE",
        "/api/sheets/{sheetKey}",
        () => this.#api.delResult("/api/sheets/{sheetKey}", { path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) } }),
      );
    } catch (err) {
      // No verdict (or one the SDK could not read): the delete may have
      // landed — re-read the sheet and drop it when it is gone.
      afterFailedWrite(err, this.#lifecycle, `sheets.${step}`, () => this.#recoverDelete(sheetKey, DELETE_REREADS));
      throw err;
    }
    this.#removeDeleted(identity, removed, actor);
    const undo = async (undoActor: Actor): Promise<void> => {
      await this.#restore(sheetKey);
      this.#touch(identity);
      if (removed) this.#insertLocal(removed);
      // No SheetDeleted on restore — the sheet is back.
      this.#invalidation.emit(new SheetListChanged(undoActor));
    };
    return { outcome: step === "delete" ? "deleted" : "reset", undo };
  }

  /**
   * Remove a sheet the server deleted: out of the cache, then
   * `SheetDeleted(sheetId)` — so a consumer asking "is the sheet I am
   * looking at gone?" reacts with a precise id check — and its live handle
   * and rows-side cache evicted (a re-open re-seeds a fresh, re-COW-able
   * ephemeral instead of the stale promoted handle), then `SheetListChanged`
   * for the aggregate views; all as `actor`.
   */
  #removeDeleted(sheetKey: string, removed: Option<Readonly<Network.UserSheet>>, actor: Actor): void {
    this.#touch(sheetKey);
    this.#removeLocal(sheetKey);
    if (removed) {
      this.#invalidation.emit(new SheetDeleted(actor, SheetId.of(removed.id)));
      this.#onSheetDeleted?.(SheetId.of(removed.id));
    }
    this.#invalidation.emit(new SheetListChanged(actor));
  }

  /**
   * A failed delete's recovery (`afterFailedWrite`): re-read the sheet
   * (`GET /api/sheets/{sheetKey}`); when the server no longer holds it the
   * delete landed — remove it as a landed delete does, announced as
   * `SheetsClient.ACTOR`. Nothing when the server still holds it, when
   * the cache no longer holds it, or once the client was disposed. When a
   * write of this list touched the sheet while the read was out (an
   * update, a reorder: the answer may predate it — the `#rereads` barrier,
   * keyed by the sheet's identity whether `sheetKey` spells its id or its
   * lookup key) the answer decides nothing: the recovery is owed, and runs
   * again once that write settled (`#owe`, at most `rereads` more times),
   * so a newer write the server refuses no longer leaves the deleted
   * sheet cached.
   */
  async #recoverDelete(sheetKey: string, rereads: number): Promise<void> {
    if (this.#lifecycle.disposed) return;
    const identity = this.#identityOf(sheetKey);
    const touched = this.#openBarrier();
    let fresh: Option<Network.UserSheetWithRows>;
    try {
      fresh = await this.#getByKey(sheetKey);
    } finally {
      this.#rereads.delete(touched);
    }
    if (this.#lifecycle.disposed) return;
    // The sheet as the cache names it now: a lookup key the cache did not
    // resolve before the read may name a cached entry since (a `list()`
    // answered while it was out) — a write addressed by its id meets the
    // barrier under that identity, and the landed delete removes it.
    const current = this.#identityOf(sheetKey);
    if (touched.has(identity) || touched.has(current)) {
      await this.#owe(current, rereads, (left) => this.#recoverDelete(sheetKey, left));
      return;
    }
    if (fresh != null) return;
    const removed = this.#byId.get(current) ?? null;
    if (removed == null) return;
    this.#removeDeleted(current, removed, SheetsClient.ACTOR);
  }

  /**
   * A failed create's or reorder's recovery (`afterFailedWrite`): re-read
   * the list (`list()`) and announce it as one `SheetListChanged`
   * (`SheetsClient.ACTOR`). Nothing once the client was disposed.
   */
  async #relist(): Promise<void> {
    if (this.#lifecycle.disposed) return;
    await this.list();
    if (this.#lifecycle.disposed) return;
    this.#invalidation.emit(new SheetListChanged(SheetsClient.ACTOR));
  }

  /** Undo a delete / reset on the server; throws `SheetWriteError("restore")`. */
  async #restore(sheetKey: string): Promise<void> {
    this.#lifecycle.assertLive("sheets.restore");
    try {
      await SheetWriteError.check(
        "restore",
        "POST",
        "/api/sheets/{sheetKey}/restore",
        () =>
          this.#api.postResult("/api/sheets/{sheetKey}/restore", {
            path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) },
          }),
      );
    } catch (err) {
      // No verdict (or one the SDK could not read): the restore may have
      // landed — re-read the sheet and list it when the server holds it.
      afterFailedWrite(err, this.#lifecycle, "sheets.restore", () => this.#recoverRestore(sheetKey));
      throw err;
    }
  }

  /**
   * A failed restore's recovery (`afterFailedWrite`): re-read the sheet
   * (`GET /api/sheets/{sheetKey}`); when the server holds it again the
   * restore landed — cache it and announce the list as one
   * `SheetListChanged` (`SheetsClient.ACTOR`). Nothing when the server
   * does not hold it, or once the client was disposed.
   */
  async #recoverRestore(sheetKey: string): Promise<void> {
    if (this.#lifecycle.disposed) return;
    const fresh = await this.#getByKey(sheetKey);
    if (fresh == null || this.#lifecycle.disposed) return;
    const { rows: _rows, ...sheet } = fresh;
    this.#touch(sheet.id);
    this.#insertLocal(sheet);
    this.#invalidation.emit(new SheetListChanged(SheetsClient.ACTOR));
  }

  /** The sheet's access list. Throws `SheetReadError("get-acl")` on failure. */
  async getAcl(sheet: SheetsClient.SheetRef): Promise<Network.UserSheetAccessSelect[]> {
    this.#lifecycle.assertLive("sheets.getAcl");
    const sheetKey = this.#keyOf(sheet);
    return SheetReadError.check(
      "get-acl",
      "GET",
      "/api/sheets/{sheetKey}/acl",
      () => this.#api.getResult("/api/sheets/{sheetKey}/acl", { path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) } }),
    );
  }

  /** Share the sheet. Once the server granted it, emits
   *  `SheetMetadataChanged` stamped with `actor` (`Actor.EXTERNAL` for a
   *  caller with no echo of its own). Throws `SheetWriteError("grant-acl")`
   *  — nothing emitted — on failure. */
  async grantAcl(
    sheet: SheetsClient.SheetRef,
    acl: Network.CreateAcl,
    actor: Actor,
  ): Promise<void> {
    this.#lifecycle.assertLive("sheets.grantAcl");
    const sheetKey = this.#keyOf(sheet);
    try {
      await SheetWriteError.check(
        "grant-acl",
        "POST",
        "/api/sheets/{sheetKey}/acl",
        () =>
          this.#api.postResult("/api/sheets/{sheetKey}/acl", {
            path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) },
            body: acl,
          }),
      );
    } catch (err) {
      // No verdict (or one the SDK could not read): the grant may have
      // landed. The list caches no access list, so its readers are told
      // (`SheetMetadataChanged` as `SheetsClient.ACTOR`) and re-read it.
      afterFailedWrite(err, this.#lifecycle, "sheets.grantAcl", () => {
        this.#invalidation.emit(new SheetMetadataChanged(SheetsClient.ACTOR, this.#refOf(sheetKey)));
        return undefined;
      });
      throw err;
    }
    this.#invalidation.emit(new SheetMetadataChanged(actor, this.#refOf(sheetKey)));
  }

  /** Unshare the sheet with `grantee.userId`. Once the server revoked it,
   *  emits `SheetMetadataChanged` stamped with `actor`. Throws
   *  `SheetWriteError("revoke-acl")` — nothing emitted — on failure. */
  async revokeAcl(
    sheet: SheetsClient.SheetRef,
    grantee: { readonly userId: UserId },
    actor: Actor,
  ): Promise<void> {
    this.#lifecycle.assertLive("sheets.revokeAcl");
    const sheetKey = this.#keyOf(sheet);
    const userId = grantee.userId;
    await SheetWriteError.check(
      "revoke-acl",
      "DELETE",
      "/api/sheets/{sheetKey}/acl",
      () =>
        this.#api.delResult("/api/sheets/{sheetKey}/acl", {
          path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) },
          body: { user_id: userId },
        }),
    );
    this.#invalidation.emit(new SheetMetadataChanged(actor, this.#refOf(sheetKey)));
  }


  /**
   * The key a sheet-list verb sends for `sheet`: a held list entry's or an
   * open handle's own id; a `SheetId` (or a `SheetKey` that knows its id) as
   * the cached entry spells it — the cache and the server agree on that
   * spelling — else its canonical form; a `SheetKey` with no id, its lookup
   * form.
   */
  #keyOf(sheet: SheetsClient.SheetRef): string {
    if (sheet instanceof SheetId) return this.#cachedKeyOf(sheet) ?? sheet.toString();
    if (sheet instanceof SheetKey) {
      const id = sheet.sheetId;
      return id == null ? sheet.toString() : this.#cachedKeyOf(id) ?? id.toString();
    }
    // A held entry or an open handle: the wire id it carries.
    return "network" in sheet ? sheet.network.id : sheet.id;
  }

  /**
   * The sheet `sheetKey` (a `#keyOf` key) names, as a sheet event names it:
   * its id — the cached entry's, or the key's own when it is an id — else
   * the lookup key and its user (`SheetKeyRef.of`).
   */
  #refOf(sheetKey: string): SheetKeyRef {
    const cached = this.#byId.get(sheetKey);
    if (cached != null) return SheetKeyRef.fromId(SheetId.of(cached.id));
    return SheetKeyRef.of(sheetKey);
  }

  /** The cached entry's own spelling of `id`, or `null` when none is cached. */
  #cachedKeyOf(id: SheetId): Option<string> {
    const direct = this.#byId.get(id.toString());
    if (direct != null) return direct.id;
    for (const key of this.#byId.keys()) {
      if (SheetId.parse(key) === id) return key;
    }
    return null;
  }

  // ── Local cache ───────────────────────────────────────────────────────

  /**
   * The cached list entry of `sheet` (`SheetsClient.SheetRef`: a held
   * entry, an open handle, a `SheetId` in any spelling, or a `SheetKey` —
   * by its id when it knows one, else by its lookup key); `null` when the
   * cache does not hold it. Reads the cache only: fill it with `list()`.
   */
  peek(sheet: SheetsClient.SheetRef): Option<Readonly<Network.UserSheet>> {
    if (sheet instanceof SheetKey) {
      const id = sheet.sheetId;
      if (id != null) return this.#peekId(id);
      const lookupKey = sheet.lookupKey;
      return lookupKey == null ? null : this.peekByLookupKey(lookupKey);
    }
    if (sheet instanceof SheetId) return this.#peekId(sheet);
    return this.#byId.get("network" in sheet ? sheet.network.id : sheet.id) ?? null;
  }

  /** The cached entry of `id`, whatever its spelling. */
  #peekId(id: SheetId): Option<Readonly<Network.UserSheet>> {
    const key = this.#cachedKeyOf(id);
    return key == null ? null : this.#byId.get(key) ?? null;
  }

  /** Find a sheet by lookup key in the local cache. */
  peekByLookupKey(lkey: string): Option<Readonly<Network.UserSheet>> {
    const id = this.#byLookupKey.get(lkey);
    return id == null ? null : this.#byId.get(id) ?? null;
  }

  /** Return all cached sheets sorted by position then title. */
  peekAll(): readonly Readonly<Network.UserSheet>[] {
    if (this.#sortedCache) return this.#sortedCache;
    const sorted = [...this.#byId.values()];
    sorted.sort(
      UserOrderable.compareOptChain((a, b) => {
        const title = (a.title ?? "").localeCompare(b.title ?? "");
        if (title !== 0) return title;
        return a.id.localeCompare(b.id);
      }),
    );
    return (this.#sortedCache = Object.freeze(sorted));
  }

  /** Return the first sheet in sorted order. */
  peekFirst(): Option<Readonly<Network.UserSheet>> {
    return this.peekAll()[0] ?? null;
  }

  // The cache writers below change what `peek` / `peekAll` read with no
  // network I/O and no verdict: they are private. The client's own verbs
  // call them; a boot payload seeds them through the host door
  // (`platformOf(client).seedBoot`, `seedSheetList`); a test
  // reaches them through the workspace-only
  // `clientInstrumentationOf(client).sheetList`, never an app's edit.

  /** Insert a sheet into the local cache. */
  #insertLocal(sheet: Network.UserSheet): void {
    this.#setEntry(sheet);
    this.#sortedCache = null;
  }

  /** Batch insert sheets into the local cache. */
  #batchInsertLocal(sheets: Network.UserSheet[]): void {
    for (const sheet of sheets) this.#setEntry(sheet);
    this.#sortedCache = null;
  }

  /** Update local cache for a sheet. Returns the updated sheet or null. */
  #updateLocal(id: string, patch: Partial<Network.UserSheet>): Option<Readonly<Network.UserSheet>> {
    const existing = this.#byId.get(id);
    if (!existing) return null;
    const updated = frozenSheet({ ...existing, ...patch });
    this.#setEntry(updated);
    this.#sortedCache = null;
    return updated;
  }

  /** Remove a sheet from the local cache. */
  #removeLocal(id: string): void {
    const sheet = this.#byId.get(id);
    if (!sheet) return;
    this.#byId.delete(id);
    if (sheet.lookupKey) this.#retireLookupKey(sheet.lookupKey, id);
    this.#sortedCache = null;
  }

  /** Cache `sheet` under its id, frozen (`frozenSheet`: every read hands
   *  out the entry itself, so no caller changes the cache by assigning a
   *  field), and index its lookup key; a lookup key the entry it replaces
   *  held and it no longer does is retired. */
  #setEntry(sheet: Network.UserSheet): void {
    const previous = this.#byId.get(sheet.id) ?? null;
    this.#byId.set(sheet.id, frozenSheet(sheet));
    const oldKey = previous?.lookupKey ?? null;
    if (oldKey && oldKey !== sheet.lookupKey) this.#retireLookupKey(oldKey, sheet.id);
    if (sheet.lookupKey) this.#byLookupKey.set(sheet.lookupKey, sheet.id);
  }

  /**
   * `id` no longer holds `lookupKey`: drop the mapping only when `id` owns
   * it — a key reassigned to another sheet stays with that sheet — and hand
   * it to another cached sheet that still carries it, if any.
   */
  #retireLookupKey(lookupKey: string, id: string): void {
    if (this.#byLookupKey.get(lookupKey) !== id) return;
    this.#byLookupKey.delete(lookupKey);
    for (const other of this.#byId.values()) {
      if (other.id !== id && other.lookupKey === lookupKey) {
        this.#byLookupKey.set(lookupKey, other.id);
        return;
      }
    }
  }
}

/**
 * `sheet` frozen, with its plain JSON members (`metadata`, deeply): the
 * sheet list's reads (`list`, `peek`, `peekAll`, …) hand out the cached
 * entry itself, frozen, so a caller changes it only through a write. Its
 * value members (ids, dates, `Rational`) are immutable already. Freezes
 * `sheet` in place and answers it.
 */
function frozenSheet(sheet: Readonly<Network.UserSheet>): Readonly<Network.UserSheet> {
  if (Object.isFrozen(sheet)) return sheet;
  freezeJson(sheet.metadata);
  return Object.freeze(sheet);
}

/** Freeze a plain JSON value and every object and array under it. */
function freezeJson(value: unknown): void {
  if (value == null || typeof value !== "object" || Object.isFrozen(value)) return;
  for (const member of Object.values(value)) freezeJson(member);
  Object.freeze(value);
}

/** A sheet's writable metadata, as the pending-write model tracks it. */
function attributesOfSheet(sheet: Network.UserSheet): Network.UserSheetAttributes {
  return {
    title: sheet.title ?? null,
    description: sheet.description ?? null,
    width: sheet.width ?? null,
    position: sheet.position ?? null,
  };
}

/** The sheet metadata fields a checked `update` writes, each replaced whole. */
const SHEET_FIELDS = ["title", "description", "width", "position"] as const;

/** A sheet metadata field a write names (`SHEET_FIELDS`). */
type SheetField = (typeof SHEET_FIELDS)[number];

/** The type bundle of the sheet-metadata instance of `Ledger` (keyed by sheet identity). */
interface SheetMetadata extends Ledger.Types {
  readonly key: string;
  readonly change: Network.UserSheetAttributes;
  readonly fields: Network.UserSheetAttributes;
  readonly field: SheetField;
  readonly delta: Network.UserSheetAttributes;
  readonly answer: unknown;
  readonly error: Error;
}

/**
 * A sheet PATCH's ledger strategy: each field replaces whole (the server's
 * semantics), a body carries each of its fields (`null` when cleared), and
 * the ledger is a pure model (no delta, no events: `SheetsClient` shows the
 * projection and announces `SheetMetadataChanged` itself).
 */
const SHEET_PATCH: Ledger.Strategy<SheetMetadata> = {
  empty: () => ({}),
  merge: (target, patch) => {
    const merged: Network.UserSheetAttributes = { ...target };
    for (const field of SHEET_FIELDS) {
      if (patch[field] !== undefined) setField(merged, field, patch);
    }
    return merged;
  },
  fieldsOf: (patch) => {
    const fields = new Set<SheetField>();
    for (const key of Object.keys(patch)) {
      const field = SHEET_FIELDS.find((f) => f === key);
      if (field != null && patch[field] !== undefined) fields.add(field);
    }
    return fields;
  },
  body: (fields, only) => {
    const body: Network.UserSheetAttributes = {};
    for (const field of only) copyField(body, field, fields);
    return body;
  },
  adopt: (fields, from, only) => {
    const out: Network.UserSheetAttributes = { ...fields };
    for (const field of only) copyField(out, field, from);
    return out;
  },
  overlay: (under, over) => SHEET_PATCH.merge(under, over),
  takeBack: () => ({}),
  delta: () => null,
  events: () => Ledger.SILENT,
  deferred: () => {
    throw new Error("sheets-update: the metadata ledger defers no announcement");
  },
  wrap: (err) => (err instanceof Error ? err : new Error(String(err))),
};

/** Set `to[field]` to `from[field]`, `null` when `from` lacks it (a body's, a base's field). */
function copyField(to: Network.UserSheetAttributes, field: SheetField, from: Network.UserSheetAttributes): void {
  switch (field) {
    case "title":
      to.title = from.title ?? null;
      return;
    case "description":
      to.description = from.description ?? null;
      return;
    case "width":
      to.width = from.width ?? null;
      return;
    case "position":
      to.position = from.position ?? null;
      return;
  }
}

/** Copy `from[field]` onto `to` (one typed field at a time; an absent one stays absent). */
function setField(
  to: Network.UserSheetAttributes,
  field: (typeof SHEET_FIELDS)[number],
  from: Network.UserSheetAttributes,
): void {
  switch (field) {
    case "title":
      if (from.title !== undefined) to.title = from.title;
      return;
    case "description":
      if (from.description !== undefined) to.description = from.description;
      return;
    case "width":
      if (from.width !== undefined) to.width = from.width;
      return;
    case "position":
      if (from.position !== undefined) to.position = from.position;
      return;
  }
}

/**
 * Types and values that go with `SheetsClient`: `DeletedSheetSummary`, `Patch`,
 * `patchBody`, `patchOf`, `Deletion`, `ReorderResult`, and 2 more.
 */
export namespace SheetsClient {
  /**
   * One soft-deleted sheet as `listDeleted` reads it
   * (`GET /api/sheets/list?deleted=true`): its id, its title (`null` for
   * none) and `deleted: true`.
   */
  export interface DeletedSheetSummary {
    readonly id: SheetId;
    readonly title: Option<string>;
    readonly deleted: boolean;
  }

  export namespace DeletedSheetSummary {
    const MODEL: Model.Object<DeletedSheetSummary> = Model.object<DeletedSheetSummary>({
      id: Field.required(Scalar.checked<string, SheetId>("SheetId", Scalar.string, SheetId.of, null)),
      title: WireDecode.nullable(Scalar.string),
      deleted: Field.required(Scalar.boolean),
    });

    /** `raw` decoded, or a thrown `WireDecodeError`. */
    export function parse(raw: Json): DeletedSheetSummary {
      return WireDecode.parse("DeletedSheetSummary", MODEL, raw);
    }
  }

  /**
   * A change to a sheet's metadata, as `sheets.update` / `updateDurable`
   * take it — the SDK's one patch vocabulary (`Patch`, as `RowPatch`,
   * `EventPatch` and `TasksClient.Patch` take it): a field the patch names
   * is set to its value, or cleared with `Patch.clear`; a field it omits is
   * left as it is. A sheet's position is not a patch field: `reorder` is the
   * one mover.
   */
  export type Patch = {
    readonly title?: Patch.Clearable<string>;
    readonly description?: Patch.Clearable<string>;
    readonly width?: Patch.Clearable<number>;
  };

  /**
   * `PATCH /api/sheets/{sheetKey}`'s body of `patch` — its one encoding: a
   * set field carries its value, a cleared one `null`, an omitted one no
   * key. A fresh object on every call.
   */
  export function patchBody(patch: Patch): Network.SheetUpdateAttributes {
    const body: Network.SheetUpdateAttributes = {};
    if (patch.title !== undefined) body.title = Patch.wire(patch.title);
    if (patch.description !== undefined) body.description = Patch.wire(patch.description);
    if (patch.width !== undefined) body.width = Patch.wire(patch.width);
    return body;
  }

  /**
   * The patch a decoded wire body (`Network.SheetUpdateAttributes`, a tool's
   * or a fixture's JSON) describes: a value sets, `null` clears
   * (`Patch.clear`), an absent key leaves the field.
   */
  export function patchOf(body: Network.SheetUpdateAttributes): Patch {
    const has = (key: keyof Network.SheetUpdateAttributes): boolean =>
      Object.hasOwn(body, key) && body[key] !== undefined;
    return {
      ...(has("title") ? { title: Patch.fromWire(body.title ?? null) } : {}),
      ...(has("description") ? { description: Patch.fromWire(body.description ?? null) } : {}),
      ...(has("width") ? { width: Patch.fromWire(body.width ?? null) } : {}),
    };
  }

  /**
   * What `reorder` did: moved only the one sheet, or recanonicalized every
   * position (`sheets`: the list with its new positions, already cached).
   */
  /**
   * What `delete` (`"deleted"`) or `resetBuiltinFork` (`"reset"`) did: the
   * server deleted the sheet (or the built-in's fork) and it was removed
   * locally. `undo(actor)` restores it (`POST /api/sheets/{sheetKey}/restore`)
   * and announces the list as `actor`; it rejects with
   * `SheetWriteError("restore")` when the server refuses, and with
   * `LonaSdk.Client.DisposedError` after `client.dispose()`. Nothing is
   * shown: the caller's notice ("Sheet deleted", with its undo) is built
   * from it, as a row's is from `RowAccessor.Deletion`.
   */
  export type Deletion = {
    readonly outcome: "deleted" | "reset";
    readonly undo: (actor: Actor) => Promise<void>;
  };

  export type ReorderResult =
    | { readonly kind: "moved" }
    | {
      readonly kind: "recanonicalized";
      readonly sheets: readonly Network.UserSheet[];
    };

  /**
   * A sheet a sheet-list verb (`get`, `peek`, `update`, `updateDurable`,
   * `reorder`, `delete`, `resetBuiltinFork`, `getAcl`, `grantAcl`,
   * `revokeAcl`) and `client.peekOpenHandle` act on: the held list entry, an
   * open
   * handle, or a typed key — never a bare string, so a lookup key cannot pass
   * where an id is meant, nor two string arguments swap.
   */
  export type SheetRef = Network.UserSheet | SheetHandle | SheetKey | SheetId;

  /**
   * The writers of a sheet list's local cache — what `peek` /
   * `peekAll` read. LOCAL ONLY: no request, no verdict. For filling the
   * cache from a server read (a boot payload); an edit goes through the
   * checked verbs (`create`, `update`, `updateDurable`, `delete`).
   */
  export interface CacheWriters {
    insert(sheet: Network.UserSheet): void;
    batchInsert(sheets: Network.UserSheet[]): void;
    update(id: string, patch: Partial<Network.UserSheet>): Option<Readonly<Network.UserSheet>>;
    remove(id: string): void;
  }
}
